import type { GetOrCreateCustomerResponse } from '@/libraries/revenueCat';
import type { MaybeRefOrGetter } from 'vue';
import type { SubscriptionInfo } from '@/features/jw-pro/types';

import { DefaultSubscriptionInfo } from '@/features/jw-pro/constants';

import { findSubscriptionBy } from '@/helpers/pro-web-helper';
import { updatePremiumStatus } from '@/features/jw-pro/helpers/updatePremiumStatus';

import { computed, onBeforeMount, readonly, ref, watch, toValue } from 'vue';

const purchaserInfo = ref<GetOrCreateCustomerResponse | null>(null);
const subscriptionInfo = ref<SubscriptionInfo | null>(null);

export enum PurchasesRestoreState {
	NotAttempted,
	Pending,
	FinishedSuccessfully,
	FinishedUnsuccessfully,
}

export function usePurchaserInfo(jwLoginId: MaybeRefOrGetter<string | undefined>) {
	const { t } = useI18n();

	const purchasesRestoreState = ref<PurchasesRestoreState>(PurchasesRestoreState.NotAttempted);

	const activeSubscription = computed(() => {
		const subscriptions = purchaserInfo.value?.subscriber?.subscriptions;
		if (!subscriptions) return null;

		return Object.values(subscriptions)[0] ?? null;
	});

	const subscriptionTimeframe = computed(() => subscriptionInfo.value?.timeframe ?? null);

	const isRecurringSubscription = computed(() => subscriptionTimeframe.value !== 'one time');

	const endDate = computed(() => {
		if (!isRecurringSubscription.value) return null;

		const unsubscribeDate = activeSubscription.value?.unsubscribe_detected_at;
		if (!unsubscribeDate) return null;

		const expirationDate = purchaserInfo.value?.subscriber?.entitlements?.Pro?.expires_date;

		// Find and show the earlier date which should be the actually relevant one
		const relevantEndDate = [unsubscribeDate, expirationDate]
			.filter(date => date != null)
			.map(date => new Date(date!))
			.toSorted((a, b) => a.getTime() - b.getTime())
			.at(0);

		return relevantEndDate ?? null;
	});

	const startDate = computed(() => {
		if (!isRecurringSubscription.value) return null;

		const originalPurchaseDate = activeSubscription.value?.original_purchase_date;
		return originalPurchaseDate ? new Date(originalPurchaseDate) : null;
	});

	const subscriptionLabel = computed(() => {
		switch (subscriptionTimeframe.value) {
			case 'monthly':
				return {
					subName: t('WEBAPP_FREEMIUM_SUBSCRIPTION_NAME_MONTHLY'),
					timeframe: t('WEBAPP_PER_MONTH'),
				};

			case 'yearly':
				return {
					subName: t('WEBAPP_FREEMIUM_SUBSCRIPTION_NAME_YEARLY'),
					timeframe: t('WEBAPP_PER_YEAR'),
				};

			case 'one time':
				return {
					subName: t('WEBAPP_FREEMIUM_SUBSCRIPTION_NAME_ONE_TIME'),
					timeframe: t('WEBAPP_ONE_TIME_PAYMENT'),
				};

			default:
				return {
					subName: t('WEBAPP_FREEMIUM_SUBSCRIPTION_NAME_MONTHLY'),
					timeframe: t('WEBAPP_PER_MONTH'),
				};
		}
	});

	const restoreButtonText = computed(() => {
		switch (purchasesRestoreState.value) {
			case PurchasesRestoreState.NotAttempted:
				return t('WEBAPP_FREEMIUM_SUBSCRIPTION_RESTORE_PURCHASES');
			case PurchasesRestoreState.Pending:
				return t('WEBAPP_FREEMIUM_SUBSCRIPTION_RESTORE_PURCHASES_PLEASE_WAIT');
			case PurchasesRestoreState.FinishedSuccessfully:
				return t('WEBAPP_FREEMIUM_SUBSCRIPTION_RESTORE_PURCHASES_SUCCESS');
			case PurchasesRestoreState.FinishedUnsuccessfully:
				return t('WEBAPP_FREEMIUM_SUBSCRIPTION_RESTORE_PURCHASES_FAILURE');
			default:
				return t('WEBAPP_FREEMIUM_SUBSCRIPTION_RESTORE_PURCHASES');
		}
	});

	async function refreshPurchaserInfo() {
		const premiumStatus = await updatePremiumStatus(toValue(jwLoginId));

		if (premiumStatus.purchaserInfo) {
			purchaserInfo.value = premiumStatus.purchaserInfo;
		}

		if (premiumStatus.subscriptionId) {
			subscriptionInfo.value = findSubscriptionBy('id', premiumStatus.subscriptionId) ?? DefaultSubscriptionInfo;
		}
	}

	onBeforeMount(async () => await refreshPurchaserInfo());

	// Cleanup when user signs out
	watch(
		() => toValue(jwLoginId),
		(newLoginId, oldLoginId) => {
			if (oldLoginId != null && newLoginId == null) {
				purchaserInfo.value = null;
				subscriptionInfo.value = null;
			}
		}
	);

	return {
		/** The raw RevenueCat purchaser information. */
		purchaserInfo: readonly(purchaserInfo),

		/**
		 * The subscription information of the current user. If none found, a default is used.
		 * @see `DefaultSubscriptionInfo`
		 */
		subscriptionInfo: readonly(subscriptionInfo),

		/** The currently active purchase subscription info if found. */
		activeSubscription,

		/**
		 * The subscription end date OR the last date before unsubscribing takes action.
		 * Can be null if none found or it was a one-time purchase.
		 */
		endDate,

		/** The date the current subscription was originally purchased on. Null if none found. */
		startDate,

		/** Yearly, Monthly, One Time, or null. */
		subscriptionTimeframe,

		/** False for 'one time' (lifetime) purchases, which have no renewal/cancellation lifecycle. */
		isRecurringSubscription,

		/** Translated subscription name and timeframe labels (e.g. "Monthly" / "per month") for `subscriptionTimeframe`. */
		subscriptionLabel,

		/** Translated label for the restore-purchases button, reflecting `purchasesRestoreState`. */
		restoreButtonText,

		/** Drives `restoreButtonText`. Set this as the restore-purchases flow progresses. */
		purchasesRestoreState,

		/**
		 * Fetches the latest purchaser information from RevenueCat.
		 *
		 * **Note:** This is fetched right before mount (onBeforeMount).
		 */
		refreshPurchaserInfo,
	};
}
